# Branding

Branding allows SEEK hirers to promote their company brand on their job ads.
Not all [SEEK ad products] include branding;
you can query the [ad product’s features] to determine if branding is available.

A brand has four components:

1. An opaque [object identifier] used to specify the brand when [posting a job ad].

2. A mandatory logo displayed on the hirer’s job ads and on SEEK’s Apply Form.
   This is represented as an [`AdvertisementBrandingImage`] with a type code of `OriginalLogo`.

3. An optional cover image displayed at the top of the hirer’s job ads.
   This is represented as an [`AdvertisementBrandingImage`] with a type code of `CoverImage`.

4. A display `name` used for the hirer’s reference when selecting and managing brands.

Brands on the SEEK Employer website are identified visually using their logo & cover image.
The brand’s name is rarely descriptive enough to uniquely identify the brand.
SEEK recommends that your software presents hirers with a paginated list of brands that incorporates their logo, cover image & name.

import { SeekBrandSelect } from './branding';

<SeekBrandSelect variant="multiple-brands" />

The SEEK API only supports [listing](#advertisementbrandings) and [querying](#advertisementbranding) brands.
Hirers must use the Logos & Brands section of the SEEK Employer website to manage their brands.

You will receive an empty array when listing brands for a hirer that has none configured.
In this scenario,
you should display a call to action in place of the brand selection control.
A localised deep link to the SEEK Employer website can be queried from the [`AdvertisementBrandingsConnection.brandManagementUrl`] field.

<SeekBrandSelect variant="no-brands" />

When a hirer chooses an ad product that supports branding,
your software is required to list their configured brands for selection.
It is highly recommended but ultimately optional for the hirer to select a brand to apply to their job ad.

Hirers can set a default brand on the SEEK Employer website,
which is applied to job ads that support branding but were posted without one selected.
This is handled transparently to your posting process.

When hirers create, update, or delete a brand, they expect the change to be reflected the next time they post a job ad.
To support this, you should query the hirer’s current brands as part of your software’s job posting flow.
If this isn’t possible you should provide a way for the hirer to manually refresh their brands from the SEEK API.

[`advertisementbrandingimage`]: /schema/named-type/AdvertisementBrandingImage
[`advertisementbrandingsconnection.brandmanagementurl`]: /schema/named-type/AdvertisementBrandingsConnection#/field/brandManagementUrl
[ad product’s features]: /schema/named-type/AdvertisementProductFeatures
[object identifier]: /graphql/seek-api-conventions#object-identifiers
[posting a job ad]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad
[seek ad products]: /use-cases/job-posting/ad-selection

## Before you begin

- SEEK recommends using a [browser token] to query brands directly from a hirer’s browser.
  For the browser token to work, you will need to include the `query:advertisement-brandings` scope in your request.

- You can also query brands from your backend using a [partner token].

[partner token]: /auth/partner-tokens
[browser token]: /auth/browser-tokens

## Operations

The SEEK API provides two queries for retrieving brands:

1. [`advertisementBrandings`](#advertisementbrandings) returns a paginated list of brands for a given hirer.

2. [`advertisementBranding`](#advertisementbranding) returns a brand for a given brand object identifier.

### advertisementBrandings

The [`advertisementBrandings` query] returns a [paginated list] of brands for a given hirer.

[`advertisementbrandings` query]: /schema/query/advertisementBrandings
[paginated list]: /graphql/seek-api-conventions#cursor-based-pagination

```graphql
query ($after: String, $first: Int, $hirerId: String!) {
  advertisementBrandings(after: $after, first: $first, hirerId: $hirerId) {
    edges {
      node {
        id {
          value
        }
        name
        images {
          typeCode
          url
        }
      }
    }
    brandManagementUrl {
      url
    }
    pageInfo {
      hasNextPage
      endCursor
    }
  }
}
```

```json
{
  "after": null,
  "first": 2,
  "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
}
```

```json
{
  "advertisementBrandings": {
    "edges": [
      {
        "node": {
          "id": {
            "value": "globalPublicTest:advertisementBranding:hirerBranding:METPmG2UG8HathjkUf4yA"
          },
          "name": "SEEK logo - Town",
          "images": [
            {
              "typeCode": "OriginalLogo",
              "url": "https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8"
            },
            {
              "typeCode": "CoverImage",
              "url": "https://image-service-cdn.seek.com.au/2df39384e6fe569f73073fe138027b6c0a1e7658"
            }
          ]
        }
      },
      {
        "node": {
          "id": {
            "value": "globalPublicTest:advertisementBranding:hirerBranding:37EWfWxhwVEGTT5BcjfE2L"
          },
          "name": "SEEK logo - No cover image",
          "images": [
            {
              "typeCode": "OriginalLogo",
              "url": "https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8"
            }
          ]
        }
      }
    ],
    "brandManagementUrl": {
      "url": "https://au.employer.seek.com/account/branding"
    },
    "pageInfo": {
      "hasNextPage": true,
      "endCursor": "globalPublicTest:advertisementBrandingCursor:indirectBranding:VtB5VXd"
    }
  }
}
```

For subsequent queries you pass the previous page’s `endCursor` as the `after` argument.
Once `hasNextPage` is `false` there are no more brands to page through.

### advertisementBranding

The [`advertisementBranding` query] returns the brand for a given brand object identifier.

[`advertisementbranding` query]: /schema/query/advertisementBranding

```graphql
query ($id: String!) {
  advertisementBranding(id: $id) {
    id {
      value
    }
    name
    images {
      typeCode
      url
    }
  }
}
```

```json
{
  "id": "globalPublicTest:advertisementBranding:hirerBranding:BYAZKaoXhS9LHFMv5gJ87e"
}
```

```json
{
  "advertisementBranding": {
    "id": {
      "value": "globalPublicTest:advertisementBranding:hirerBranding:BYAZKaoXhS9LHFMv5gJ87e"
    },
    "name": "SEEK logo - Laptop",
    "images": [
      {
        "typeCode": "OriginalLogo",
        "url": "https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8"
      },
      {
        "typeCode": "CoverImage",
        "url": "https://image-service-cdn.seek.com.au/f33d107cd083880157439477e7790edd629ed50a"
      }
    ]
  }
}
```
